import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createErrorLog,
  createSchedule,
  deleteSchedule,
  getClientByEmail,
  getExports,
  getPlans,
  getSchedules,
  getTimeZones,
} from "../../../api";
import { GetSecret } from "../../../api/trello";
import { delay } from "../../../utils";
import { toast } from "react-toastify";

const initialState = {
  isAuth: false,
  plan: null,
  plans: [],
  plansFetched: false,
  plans_loading: false,
  mySchedules: [],
  schedules: [],
  exports: [],
  get_exports_loading: false,
  post_schedules_success: false,
  delete_schedules_loading: false,
  schedules_loading: false,
  schedulesFetched: false,
  errorOnAuthRequest: false,
  post_schedules_loading: false,
  isClientFetched: false,
  monthTotalTasks: 0,
  monthUsedTasks: 0,
  allTimeUsedTasks: 0,
  qntTarefasPermitidasMes: 0,
  timeZonesFetched: false,
  timeZonesLoading: false,
  timeZones: [],
  usedSchedules: 0,
};

export const fetchClientData = createAsyncThunk(
  "user/fetchClientData",
  async (_, { getState, rejectWithValue }) => {
    const { email, boardId, token } = getState().trello;
    
    try {
      const response = await getClientByEmail(email, token);

      response.boardId = boardId;

      return response;
    } catch (error) {
      const err = {
        message: error?.message,
        tracer: error,
        email,
        url: error?.url || "not found",
      };

      await createErrorLog(err);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPlansData = createAsyncThunk(
  "user/fetchPlansData",
  async (_, { getState, rejectWithValue }) => {
    const { email } = getState().trello;
    try {
      const response = await getPlans();
      return response;
    } catch (error) {
      const err = {
        message: error?.message,
        tracer: error,
        email,
        url: error?.url || "not found",
      };

      await createErrorLog(err);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSchedules = createAsyncThunk(
  "user/fetchSchedulesData",
  async (_, { getState, rejectWithValue }) => {
    const { email } = getState().trello;
    try {
      const response = await getSchedules();
      return response;
    } catch (error) {
      const err = {
        message: error?.message,
        tracer: error,
        email,
        url: error?.url || "not found",
      };

      await createErrorLog(err);
      return rejectWithValue(error.message);
    }
  }
);

export const postSchedules = createAsyncThunk(
  "user/postSchedules",
  async (data, { getState, rejectWithValue, dispatch }) => {
    try {
      const t = window.TrelloPowerUp.iframe({
        appKey: "035539076201571631d2a69fa82de83d",
        appName: "Board2Export by Ezy PowerUps",
      });
      const { email } = getState().trello;
      const secret = await GetSecret(t);
      if (!secret) throw new Error("Failed to get Trello secret");

      window.Trello.setToken(secret);

      const board = await t.board("all");
      const { id, name } = board;

      const requestData = {
        textEmailCliente: email,
        textEmail: data.textEmail,
        textBoardId: id,
        nameBoard: name,
        agendaEscalas: data.agenda,
        layout: data.layout,
        TimeZoneCustomerId: data.TimeZoneCustomerId,
        typeExportacao: data.typeExportacao,
      };

      await createSchedule(requestData);
      await delay(2000);
      await dispatch(fetchClientData());
      t.alert({
        message: "Your appointment has been successfully scheduled.",
        duration: 6,
      });
      return true;
    } catch (error) {
      const err = {
        message: error?.message,
        tracer: error,
        email: data?.textEmail,
        url: error?.url || "not found",
      };
      toast.error(error.message);
      await createErrorLog(err);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteScheduleRequest = createAsyncThunk(
  "user/deleteScheduleRequest",
  async (id, { getState, rejectWithValue, dispatch }) => {
    const { email } = getState().trello;
    try {
      await deleteSchedule(id);
      await delay(2000);
      await dispatch(fetchClientData());
      return id;
    } catch (error) {
      const err = {
        message: error?.message || "Erro desconhecido",
        tracer: error,
        email,
        url: "/agenda/" + id || "URL não encontrada",
      };
      toast.error(err.message);
      await createErrorLog(err);
      return rejectWithValue(err.message);
    }
  }
);

export const fetchTimeZones = createAsyncThunk(
  "user/fetchTimeZones",
  async (_, { getState, rejectWithValue }) => {
    const { email } = getState().trello;
    try {
      const response = await getTimeZones();
      return response;
    } catch (error) {
      const err = {
        message: error?.message,
        tracer: error,
        email,
        url: error?.url || "not found",
      };

      await createErrorLog(err);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchExports = createAsyncThunk(
  "user/fetchExports",
  async ({ currentMonth, currentYear }, { getState, rejectWithValue }) => {
    const { email } = getState().trello;
    try {
      const response = await getExports(email, currentMonth, currentYear);
      return response;
    } catch (error) {
      const err = {
        message: error?.message,
        tracer: error,
        email,
        url: error?.url || "not found",
      };

      await createErrorLog(err);
      return rejectWithValue(error.message);
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientData.pending, (state) => {
        state.errorOnAuthRequest = false;
      })
      .addCase(fetchClientData.fulfilled, (state, action) => {
        state.errorOnAuthRequest = false;
        const clientData = action.payload ?? {};
      
        // Marca que já tentamos buscar o cliente
        state.isClientFetched = true;
      
        // Total de agendamentos já usados (ou zero se não vier)
        state.usedSchedules = clientData.numeroAgendas ?? 0;
      
        // Monta mySchedules apenas se for array, filtrando pelo board atual
        const agendas = Array.isArray(clientData.agendaGetViewModels)
          ? clientData.agendaGetViewModels
          : [];
        state.mySchedules = agendas.filter(
          (s) => s.textBoardId === clientData.boardId
        );
      
        // Garante que assinaturasGetViewModels seja sempre array
        const assinaturas = Array.isArray(clientData.assinaturasGetViewModels)
          ? clientData.assinaturasGetViewModels
          : [];
      
        // Encontra a assinatura ativa, protegendo contra sub nulo
        const activeSubscription = assinaturas.find((sub) => sub?.ativo);
      
        if (activeSubscription) {
          state.isAuth = true;
          state.plan = activeSubscription.plano;
        } else {
          state.isAuth = false;
          state.plan = null;
        }
      })      
      .addCase(fetchClientData.rejected, (state) => {
        state.isClientFetched = true;
        state.errorOnAuthRequest = true;
      })
      .addCase(fetchPlansData.pending, (state) => {
        state.plans_loading = true;
        state.plansFetched = false;
      })
      .addCase(fetchPlansData.fulfilled, (state, action) => {
        state.plans_loading = false;
        state.plansFetched = true;
        state.plans = action.payload;
      })
      .addCase(fetchPlansData.rejected, (state) => {
        state.plans_loading = false;
        state.plansFetched = true;
        state.plans = [];
      })
      .addCase(fetchTimeZones.pending, (state) => {
        state.timeZonesLoading = true;
        state.timeZonesFetched = false;
      })
      .addCase(fetchTimeZones.fulfilled, (state, action) => {
        state.timeZonesLoading = false;
        state.timeZonesFetched = true;
        state.timeZones = action.payload;
      })
      .addCase(fetchTimeZones.rejected, (state) => {
        state.timeZonesLoading = false;
        state.timeZonesFetched = true;
        state.timeZones = [];
      })
      .addCase(fetchSchedules.pending, (state) => {
        state.schedules_loading = true;
        state.schedulesFetched = false;
      })
      .addCase(fetchSchedules.fulfilled, (state, action) => {
        state.schedules_loading = false;
        state.schedulesFetched = true;
        state.schedules = action.payload;
      })
      .addCase(fetchSchedules.rejected, (state) => {
        state.schedules_loading = false;
        state.schedulesFetched = true;
        state.schedules = [];
      })
      .addCase(postSchedules.pending, (state) => {
        state.post_schedules_loading = true;
        state.post_schedules_success = false;
      })
      .addCase(postSchedules.fulfilled, (state) => {
        state.post_schedules_loading = false;
        state.post_schedules_success = true;
      })
      .addCase(postSchedules.rejected, (state) => {
        state.post_schedules_loading = false;
        state.post_schedules_success = false;
      })
      .addCase(deleteScheduleRequest.pending, (state) => {
        state.delete_schedules_loading = true;
      })
      .addCase(deleteScheduleRequest.fulfilled, (state, action) => {
        state.delete_schedules_loading = false;
        const id = action.payload;
        state.mySchedules = state.mySchedules.filter((item) => item.id !== id);
      })
      .addCase(deleteScheduleRequest.rejected, (state) => {
        state.delete_schedules_loading = false;
      })
      .addCase(fetchExports.pending, (state) => {
        state.get_exports_loading = true;
      })
      .addCase(fetchExports.fulfilled, (state, action) => {
        state.get_exports_loading = false;
        state.exports = action.payload.exportacaoGetViewModels;
      })
      .addCase(fetchExports.rejected, (state) => {
        state.get_exports_loading = false;
      });
  },
});

export default userSlice.reducer;
